import { Fragment, useEffect } from 'react';
import { onCLS, onFCP, onFID, onINP, onLCP, onTTFB } from 'web-vitals';

import { CONVERSIO_SITE_CODE } from 'core/constants';
import PropTypes from 'prop-types';
import Script from 'next/script';
import { useCulture } from 'context/CultureContext';

const ScriptContainer = ({ children, breadcrumbList = [], delayLowPriorityScript = 4000 }) => {
    const { culture } = useCulture();
    const siteCode = CONVERSIO_SITE_CODE[culture];

    useEffect(() => {
        const sendToGTM = (name, delta, id) => {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: 'web-vitals',
                event_category: 'Performance',
                event_action: name.name,
                event_value: Math.round(name.name === 'CLS' ? name.delta * 1000 : name.delta),
                event_label: name.id
            });

            window.conversioGo((cn) => {
                const value = Math.round(name.name === 'CLS' ? name.delta * 1000 : name.delta);
                cn.trackConversion(`web-vitals-${name.name}`, value);
            });
        };
        [onCLS, onFID, onLCP, onFCP, onTTFB, onINP].forEach(metric => metric(sendToGTM));
    }, []);

    return (
        <Fragment>
            <Script id="js-cookies" strategy="lazyOnload">
                {/* Original file: /npm/js-cookie@2.2.1/src/js.cookie.js */}
                {`!function(e){var n;if("function"==typeof define&&define.amd&&(define(e),n=!0),"object"==typeof exports&&(module.exports=e(),n=!0),!n)
                {var t=window.Cookies,o=window.Cookies=e();o.noConflict=function(){return window.Cookies=t,o}}}(function(){function e(){for(var e=0,n={};
                e<arguments.length;e++){var t=arguments[e];for(var o in t)n[o]=t[o]}return n}function n(e){return e.replace(/(%[0-9A-Z]{2})+/g,decodeURIComponent)}
                return function t(o){function r(){}function i(n,t,i){if("undefined"!=typeof document){"number"==typeof(i=e({path:"/"},r.defaults,i))
                .expires&&(i.expires=new Date(1*new Date+864e5*i.expires)),i.expires=i.expires?i.expires.toUTCString():"";try{var c=JSON.stringify(t);
                /^[{[]/.test(c)&&(t=c)}catch(e){}t=o.write?o.write(t,n):encodeURIComponent(String(t)).replace(/%(23|24|26|2B|3A|3C|3E|3D|2F|3F|40|5B|5D|5E|60|7B|7D|7C)/g,
                decodeURIComponent),n=encodeURIComponent(String(n)).replace(/%(23|24|26|2B|5E|60|7C)/g,decodeURIComponent).replace(/[()]/g,escape);var f="";for(var u in i)i[u]&&
                (f+="; "+u,!0!==i[u]&&(f+="="+i[u].split(";")[0]));return document.cookie=n+"="+t+f}}function c(e,t){if("undefined"!=typeof document){for(var r={},
                i=document.cookie?document.cookie.split("; "):[],c=0;c<i.length;c++){var f=i[c].split("="),u=f.slice(1).join("=");t||'"'!==u.charAt(0)||(u=u.slice(1,-1));
                try{var a=n(f[0]);if(u=(o.read||o)(u,a)||n(u),t)try{u=JSON.parse(u)}catch(e){}if(r[a]=u,e===a)break}catch(e){}}return e?r[e]:r}}return r.set=i,r.get=function(e)
                {return c(e,!1)},r.getJSON=function(e){return c(e,!0)},r.remove=function(n,t){i(n,"",e(t,{expires:-1}))},r.defaults={},r.withConverter=t,r}(function(){})});`}
            </Script>
            {/* <Script
                src="https://d3po9jkuwb69jo.cloudfront.net/static/js/compiled/react/conversio.d6367d17ee1c949f2332.min.js"
                strategy="lazyOnload"
            />
            <Script
                src="https://conversio.ceca.co/js/tracker.min.js?v=4"
                strategy="lazyOnload"
            /> */}
            <Script
                id="helpers"
                strategy="beforeInteractive"
                dangerouslySetInnerHTML={{
                    __html: `
                            function createScript(src) {
                                var scriptParent = document.getElementsByTagName('script')[0];
                                var element = document.createElement('script');
                                element.defer = true;
                                element.src = src;
                                scriptParent.parentNode.insertBefore(element, scriptParent);
                            }

                            window.t = function(s) {
                                return gettext(s);
                            }

                            window.defer_until = function(fn_rule, fn) {
                                if (fn_rule()) {
                                    fn();
                                } else {
                                    setTimeout(function() { defer_until(fn_rule, fn) }, 10);
                                }
                            }

                            window.fn_conversio = function(fn) {
                                if (window.conversio) {
                                    fn(window.conversio);
                                } else {
                                    window.conversioList = window.conversioList || [];
                                    window.conversioList.push(fn)
                                }
                            }

                            window.conversioGo = function(fn) {
                                window.conversioQueue = window.conversioQueue || [];
                                window.conversioQueue.push(fn)
                            }
                            `
                }}
            />

            <Script
                strategy="lazyOnload"
                id="google_manager"
                dangerouslySetInnerHTML={{
                    __html: `
                        try {
                            // Delay the start of GTM 1,5 seconds
                            setTimeout(function() {
                                (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                                new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                                j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.defer=true;j.src=
                                'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                                })(window,document,'script','dataLayer','GTM-KTCJ9D');
                            }, ${delayLowPriorityScript});
                        } catch (err) {}
                    `
                }}
            />
            <Script
                strategy="lazyOnload"
                id="window_ceca_conversio"
                dangerouslySetInnerHTML={{
                    __html: `
                        // Core Conversio
                        window.conversioList = window.conversioList || [];
                        window.defer_until(function() {
                            return window.Ceca !== undefined && window.Ceca.conversio !== undefined;
                        }, function() {
                            window.conversio = new Ceca.conversio.Conversio();
                        })

                        // Conversio External
                        var configConversio = {
                            siteCode: "${siteCode}",
                            livePingFrequency: 20000
                        };
                        if ("${siteCode}" !== "undefined") {
                            window.defer_until(function() {
                                return window.ceca !== undefined && window.ceca.Conversio !== undefined;
                            }, function() {
                                window.cecaConversio = new window.ceca.Conversio(configConversio);
                            })
                        }

                        // Insert the conversio scripts
                        try {
                            setTimeout(() => {
                                createScript('https://d3po9jkuwb69jo.cloudfront.net/static/js/compiled/react/conversio.d6367d17ee1c949f2332.min.js');
                                createScript('https://conversio.ceca.co/js/tracker.min.js?v=4');
                            }, ${delayLowPriorityScript});
                        } catch (err) {}
                    `
                }}
            />
            <Script
                type="application/ld+json"
                id="id_breadcrumb"
                strategy="lazyOnload"
            >
                {JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'BreadcrumbList',
                    itemListElement: breadcrumbList
                })}
            </Script>
            {children}
            {/* Google Tag Manager */}
            <noscript>
                <iframe
                    rel="preconnect"
                    src="https://www.googletagmanager.com/ns.html?id=GTM-KTCJ9D"
                    height="0"
                    width="0"
                    style={{ display: 'none', visibility: 'hidden' }}>
                </iframe>
            </noscript>
            {/* End Google Tag Manager */}
        </Fragment>
    );
};

ScriptContainer.propTypes = {
    children: PropTypes.any,
    breadcrumbList: PropTypes.array,
    delayLowPriorityScript: PropTypes.number
};

export default ScriptContainer;
